import React, { useCallback, useEffect, useState } from 'react';
import {
  disableReportUser,
  dismissReport,
  enableReportUser,
  getReportsApiPayload,
  isReportsApiSuccess,
  listReportedUsers,
  searchReportUsers,
} from '../../services/reportsApi';
import {
  formatDateTime,
  formatPhone,
  PAGE_SIZE,
  STATUS_FILTERS,
  useReportSession,
} from './reportHelpers';
import SearchIcon from '@mui/icons-material/Search';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

const VIEW_SEARCH = 'search';
const VIEW_REPORTED = 'reported';

function ReportUsers() {
  const { showSnackbar, handleSessionExpired, snackbarEl } = useReportSession();

  const [view, setView] = useState(VIEW_REPORTED);
  const [status, setStatus] = useState('pending');
  const [query, setQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [offset, setOffset] = useState(0);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [confirm, setConfirm] = useState(null);
  const [reason, setReason] = useState('');
  const [softDeleteFeeds, setSoftDeleteFeeds] = useState(true);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res =
        view === VIEW_SEARCH
          ? await searchReportUsers({ q: query, limit: PAGE_SIZE, offset })
          : await listReportedUsers({
              status,
              q: query || undefined,
              limit: PAGE_SIZE,
              offset,
            });

      if (handleSessionExpired(res)) return;
      if (!isReportsApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to load users', 'error');
        setUsers([]);
        setTotal(0);
        setHasMore(false);
        return;
      }

      const payload = getReportsApiPayload(res) || {};
      setUsers(payload.users || []);
      setTotal(payload.total ?? 0);
      setHasMore(Boolean(payload.pagination?.hasMore));
    } finally {
      setLoading(false);
    }
  }, [view, query, status, offset, handleSessionExpired, showSnackbar]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const runSearch = () => {
    setOffset(0);
    setQuery(searchInput.trim());
  };

  const openDisable = (user) => {
    setReason('');
    setSoftDeleteFeeds(true);
    setConfirm({ type: 'disable', user });
  };

  const openEnable = (user) => {
    setReason('');
    setConfirm({ type: 'enable', user });
  };

  const openDismiss = (user) => {
    const reportId = user.latestReportId;
    if (!reportId) {
      showSnackbar('No report id available to dismiss', 'error');
      return;
    }
    setReason('');
    setConfirm({ type: 'dismiss', user, reportId });
  };

  const getUserId = (user) => user.userId ?? user.id;
  const getUsername = (user) => user.username || '—';
  const getName = (user) => user.name || '—';
  const getPhone = (user) => user.phone;
  const getEmail = (user) => user.email;
  const getAvatar = (user) => user.profilePicUrl || user.profilepicurl;
  const isDisabled = (user) =>
    Boolean(user.isDisabled ?? user.is_disabled) ||
    user.userStatus === false ||
    user.status === false ||
    Boolean(user.userDeletedAt || user.deleted_at);
  const isPhoneBlocked = (user) =>
    Boolean(user.isPhoneBlocked ?? user.is_phone_blocked);

  const applyOptimisticDisable = (userId, disabled) => {
    setUsers((prev) =>
      prev.map((u) => {
        if (Number(getUserId(u)) !== Number(userId)) return u;
        if (view === VIEW_SEARCH) {
          return {
            ...u,
            is_disabled: disabled,
            is_phone_blocked: disabled,
            status: !disabled,
            deleted_at: disabled ? new Date().toISOString() : null,
          };
        }
        return {
          ...u,
          isDisabled: disabled,
          isPhoneBlocked: disabled,
          userStatus: !disabled,
          userDeletedAt: disabled ? new Date().toISOString() : null,
          pendingCount: disabled ? 0 : u.pendingCount,
        };
      })
    );
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    const userId = getUserId(confirm.user);
    setActionLoading(true);
    try {
      let res;
      if (confirm.type === 'disable') {
        res = await disableReportUser(userId, {
          reason: reason.trim() || undefined,
          softDeleteFeeds,
        });
      } else if (confirm.type === 'enable') {
        res = await enableReportUser(userId, {
          reason: reason.trim() || undefined,
        });
      } else {
        res = await dismissReport(confirm.reportId, {
          reason: reason.trim() || undefined,
        });
      }

      if (handleSessionExpired(res)) return;
      if (!isReportsApiSuccess(res)) {
        showSnackbar(res?.message || 'Action failed', 'error');
        return;
      }

      const payload = getReportsApiPayload(res);
      if (confirm.type === 'disable') {
        applyOptimisticDisable(userId, true);
        const feeds = payload?.feedsSoftDeleted;
        showSnackbar(
          feeds != null
            ? `User disabled · ${feeds} posts hidden`
            : 'User disabled'
        );
      } else if (confirm.type === 'enable') {
        applyOptimisticDisable(userId, false);
        showSnackbar('User enabled (feeds were not restored)');
      } else {
        showSnackbar('Report dismissed');
        loadUsers();
      }
      setConfirm(null);
    } finally {
      setActionLoading(false);
    }
  };

  const confirmTitle =
    confirm?.type === 'disable'
      ? 'Disable user'
      : confirm?.type === 'enable'
        ? 'Enable user'
        : 'Dismiss report';

  const confirmCopy = (() => {
    if (!confirm) return '';
    const uname = getUsername(confirm.user);
    const phone = formatPhone(getPhone(confirm.user));
    if (confirm.type === 'disable') {
      return `Disable @${uname} (${phone})? They will be logged out, their posts hidden (if selected), and this phone cannot create a new account.`;
    }
    if (confirm.type === 'enable') {
      return `Enable @${uname}? This restores login and lifts the phone block. Soft-deleted posts are not restored.`;
    }
    return `Dismiss the latest report for @${uname} without disabling the account?`;
  })();

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Tabs
          value={view}
          onChange={(_, v) => {
            setView(v);
            setOffset(0);
            setQuery('');
            setSearchInput('');
          }}
        >
          <Tab label="Reported users" value={VIEW_REPORTED} />
          <Tab label="Search all users" value={VIEW_SEARCH} />
        </Tabs>
      </Stack>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ md: 'center' }}
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          placeholder={
            view === VIEW_SEARCH
              ? 'Search by id, username, phone, or email'
              : 'Filter reported users…'
          }
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') runSearch();
          }}
          sx={{ minWidth: 280, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" onClick={runSearch}>
          Search
        </Button>
        {view === VIEW_REPORTED && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {STATUS_FILTERS.map((f) => (
              <Chip
                key={f.value}
                label={f.label}
                clickable
                color={status === f.value ? 'primary' : 'default'}
                variant={status === f.value ? 'filled' : 'outlined'}
                onClick={() => {
                  setStatus(f.value);
                  setOffset(0);
                }}
              />
            ))}
          </Stack>
        )}
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Phone / Email</TableCell>
              {view === VIEW_REPORTED && <TableCell>Reports</TableCell>}
              {view === VIEW_REPORTED && <TableCell>Latest reason</TableCell>}
              {view === VIEW_SEARCH && <TableCell>Created</TableCell>}
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">
                    {view === VIEW_SEARCH
                      ? query
                        ? 'No users matched your search.'
                        : 'Search by id, username, phone, or email.'
                      : 'No reported users for this filter.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => {
                const disabled = isDisabled(user);
                const phoneBlocked = isPhoneBlocked(user);
                return (
                  <TableRow key={getUserId(user)} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar src={getAvatar(user) || undefined} alt="">
                          {(getUsername(user) || '?')[0]?.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            @{getUsername(user)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getName(user)} · ID {getUserId(user)}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatPhone(getPhone(user))}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getEmail(user) || '—'}
                      </Typography>
                    </TableCell>
                    {view === VIEW_REPORTED && (
                      <TableCell>
                        <Typography variant="body2">
                          {user.reportCount ?? 0} total
                        </Typography>
                        {(user.pendingCount ?? 0) > 0 && (
                          <Chip
                            size="small"
                            color="warning"
                            label={`${user.pendingCount} pending`}
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </TableCell>
                    )}
                    {view === VIEW_REPORTED && (
                      <TableCell sx={{ maxWidth: 240 }}>
                        <Typography variant="body2" noWrap title={user.latestReason || ''}>
                          {user.latestReason || '—'}
                        </Typography>
                      </TableCell>
                    )}
                    {view === VIEW_SEARCH && (
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(user.created_at)}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {disabled ? (
                          <Chip size="small" color="error" label="Disabled" />
                        ) : (
                          <Chip size="small" color="success" label="Active" />
                        )}
                        {phoneBlocked && (
                          <Chip size="small" color="default" label="Phone blocked" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {view === VIEW_REPORTED &&
                          (user.pendingCount ?? 0) > 0 &&
                          user.latestReportId && (
                            <Button
                              size="small"
                              onClick={() => openDismiss(user)}
                            >
                              Dismiss
                            </Button>
                          )}
                        {disabled ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={() => openEnable(user)}
                          >
                            Enable
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={() => openDisable(user)}
                          >
                            Disable
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          {total} total · showing {offset + 1}–
          {Math.min(offset + PAGE_SIZE, total || offset)}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            disabled={offset === 0 || loading}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
          >
            Previous
          </Button>
          <Button
            disabled={!hasMore || loading}
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
          >
            Next
          </Button>
        </Stack>
      </Stack>

      <Dialog
        open={Boolean(confirm)}
        onClose={() => !actionLoading && setConfirm(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{confirmCopy}</DialogContentText>
          <TextField
            fullWidth
            size="small"
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            sx={{ mb: confirm?.type === 'disable' ? 1 : 0 }}
          />
          {confirm?.type === 'disable' && (
            <FormControlLabel
              control={
                <Switch
                  checked={softDeleteFeeds}
                  onChange={(e) => setSoftDeleteFeeds(e.target.checked)}
                />
              }
              label="Also soft-delete this user's posts"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={actionLoading} onClick={() => setConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirm?.type === 'enable' ? 'success' : 'error'}
            disabled={actionLoading}
            onClick={handleConfirm}
          >
            {actionLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : confirm?.type === 'disable' ? (
              'Disable'
            ) : confirm?.type === 'enable' ? (
              'Enable'
            ) : (
              'Dismiss'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {snackbarEl}
    </Box>
  );
}

export default ReportUsers;
